/**
 * PrincipalRef — the canonical, DISPLAY-ONLY wire shape for a "who" (a human end-user, a
 * human admin, or an automation account), emitted uniformly by the backend across owner /
 * created_by / actor fields so every admin surface renders a principal the same way: a typed
 * name, never a bare UUID. This is the single source of truth for the wire shape; every
 * feature that names a principal reuses `principalRefSchema` instead of re-declaring it, and
 * renders it through the shared `<PrincipalRef>` component in `@pureid/ui`.
 *
 * Honesty contract (mirrors the backend resolver): `deleted` ⇒ the owner row is gone
 * (label/email empty) and the UI shows an honest "Deleted <type>" placeholder, never a
 * fabricated name. The backend OMITS the field entirely on a transient resolve fault (so the
 * UI falls back to the raw id) — which is why the field is `.optional()` at every embed site.
 *
 * `id` is the principal's own id, carried so a consumer can fall back to it (and pick a
 * detail-page link by `kind`) without a separate prop.
 */

import { z } from "zod";

export const principalKindSchema = z.enum(["user", "admin", "automation_account"]);
export type PrincipalKind = z.infer<typeof principalKindSchema>;

export const principalRefSchema = z.object({
  // Tolerant like the other display fields: an older/cached owner payload (pre-`id`, or any
  // future producer that omits it) must NOT fail the whole enclosing parse. The renderer
  // falls back to a caller-supplied raw id, so an empty id degrades gracefully, never throws.
  id: z.string().optional().default(""),
  kind: principalKindSchema,
  label: z.string().optional().default(""),
  email: z.string().optional().default(""),
  // Automation-account only: the human admin who created it (the accountability chain).
  created_by_label: z.string().optional().default(""),
  // The owner row no longer exists; render an honest placeholder, not a fabricated name.
  deleted: z.boolean().optional().default(false),
});

export type PrincipalRef = z.infer<typeof principalRefSchema>;
